import { useEffect } from 'react'
import type { RuntimeTheme } from './types'

/**
 * React lifecycle helper for cached + remote runtime theme hydration.
 *
 * @param params Reader/apply/fetch callbacks used to bootstrap active theme.
 */
export const useRuntimeThemeLifecycle = ({
  readCachedTheme,
  applyTheme,
  fetchTheme,
  getFallbackTheme,
}: {
  readCachedTheme: () => RuntimeTheme | null
  applyTheme: (theme: RuntimeTheme) => void
  fetchTheme: () => Promise<RuntimeTheme>
  getFallbackTheme: () => RuntimeTheme
}) => {
  useEffect(() => {
    let active = true
    const cached = readCachedTheme()

    if (cached) {
      applyTheme(cached)
    }

    const run = async () => {
      try {
        const runtimeTheme = await fetchTheme()
        if (!active) return
        applyTheme(runtimeTheme)
      } catch {
        if (!cached) {
          applyTheme(getFallbackTheme())
        }
      }
    }

    void run()

    return () => {
      active = false
    }
  }, [applyTheme, fetchTheme, getFallbackTheme, readCachedTheme])
}
